import createDebug from 'debug';
import {
	AlertTriangle,
	Check,
	ChevronDown,
	ChevronUp,
	Download,
	FileAudio,
	FileImage,
	FileText,
	FileVideo,
	Loader2,
	RefreshCw,
	X,
} from 'lucide-react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { DownloadToolPicker } from '@/components/DownloadToolPicker';
import { MediaPreview } from '@/components/MediaPreview';
import { Button } from '@/components/ui/button';
import {
	formatBytes,
	useCancelDownload,
	useDownloadStatus,
	useRetryDownload,
	useTriggerDownload,
} from '@/hooks/useDownloads';

const debug = createDebug('app:download-section');

function fileIcon(mimeType: string) {
	if (mimeType.startsWith('video/')) return FileVideo;
	if (mimeType.startsWith('image/')) return FileImage;
	if (mimeType.startsWith('audio/')) return FileAudio;
	return FileText;
}

export interface DownloadSectionHandle {
	trigger: (tool?: string) => void;
}

interface DownloadSectionProps {
	linkId: number;
}

export const DownloadSection = forwardRef<DownloadSectionHandle, DownloadSectionProps>(function DownloadSection({ linkId }, ref) {
	const { download } = useDownloadStatus(linkId);
	const { trigger, isPending: isTriggering } = useTriggerDownload();
	const { cancel } = useCancelDownload();
	const { retry } = useRetryDownload();
	const [error, setError] = useState<string | null>(null);
	const [showToolPicker, setShowToolPicker] = useState(false);
	const [showFileList, setShowFileList] = useState(false);

	const handleTrigger = useCallback(async (tool?: string) => {
		setError(null);
		try {
			await trigger(linkId, tool);
			debug('download triggered for link %d (tool=%s)', linkId, tool ?? 'auto');
		} catch (err) {
			const msg = err instanceof Error ? err.message : String(err);
			setError(msg);
			debug('trigger error: %s', msg);
		}
	}, [linkId, trigger]);

	// Expose trigger to parent via ref
	useImperativeHandle(ref, () => ({
		trigger: (tool?: string) => { handleTrigger(tool); },
	}));

	const handleCancel = useCallback(async () => {
		try {
			await cancel(linkId);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		}
	}, [linkId, cancel]);

	const handleRetry = useCallback(async () => {
		setError(null);
		try {
			await retry(linkId);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		}
	}, [linkId, retry]);

	function handleDownloadClick(e: React.MouseEvent) {
		if (e.shiftKey) {
			e.preventDefault();
			setShowToolPicker(true);
		} else {
			handleTrigger();
		}
	}

	// Loading state
	if (download === undefined) return null;

	// No download exists - show trigger button
	if (download === null) {
		return (
			<div className="rounded-lg border bg-muted/30 p-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Download className="size-4 text-muted-foreground" />
						<span className="text-sm font-mono text-muted-foreground">No download</span>
					</div>
					<div className="relative">
						<Button
							variant="outline"
							size="sm"
							className="text-xs font-mono"
							onClick={handleDownloadClick}
							disabled={isTriggering}
						>
							{isTriggering ? (
								<Loader2 className="size-3 mr-1 animate-spin" />
							) : (
								<Download className="size-3 mr-1" />
							)}
							{isTriggering ? 'QUEUING...' : '[DOWNLOAD]'}
						</Button>
						{showToolPicker && (
							<DownloadToolPicker
								onDownload={(tool) => {
									setShowToolPicker(false);
									handleTrigger(tool);
								}}
								onClose={() => setShowToolPicker(false)}
								disabled={isTriggering}
							/>
						)}
					</div>
				</div>
				{error && (
					<div className="mt-2 flex items-start gap-2 text-sm text-destructive">
						<AlertTriangle className="size-4 mt-0.5 shrink-0" />
						<span>{error}</span>
					</div>
				)}
			</div>
		);
	}

	// Pending
	if (download.status === 'PENDING') {
		return (
			<div className="rounded-lg border bg-muted/30 p-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Loader2 className="size-4 animate-spin text-amber-500" />
						<span className="text-sm font-mono text-amber-500">Queued...</span>
						{download.tool && (
							<span className="text-xs text-muted-foreground">({download.tool})</span>
						)}
					</div>
					<Button
						variant="ghost"
						size="sm"
						className="text-xs font-mono text-muted-foreground hover:text-destructive"
						onClick={handleCancel}
					>
						<X className="size-3 mr-1" />
						[CANCEL]
					</Button>
				</div>
			</div>
		);
	}

	// Downloading
	if (download.status === 'DOWNLOADING') {
		return (
			<div className="rounded-lg border bg-muted/30 p-4">
				<div className="flex items-center gap-2">
					<Loader2 className="size-4 animate-spin text-blue-500" />
					<span className="text-sm font-mono text-blue-500">Downloading...</span>
					{download.tool && (
						<span className="text-xs text-muted-foreground">({download.tool})</span>
					)}
				</div>
			</div>
		);
	}

	// Completed
	if (download.status === 'COMPLETED') {
		const hasMedia = download.files.some(
			(f) => f.mimeType.startsWith('image/') || f.mimeType.startsWith('video/')
		);
		const nonMediaFiles = download.files.filter(
			(f) => !f.mimeType.startsWith('image/') && !f.mimeType.startsWith('video/')
		);

		return (
			<div className="space-y-3">
				{/* Media preview (images/video inline) */}
				{hasMedia && (
					<MediaPreview files={download.files} linkId={linkId} />
				)}

				{/* Status bar */}
				<div className="rounded-lg border bg-muted/30 p-3">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Check className="size-4 text-emerald-500" />
							<span className="text-xs font-mono text-emerald-500">
								{download.files.length} file{download.files.length !== 1 ? 's' : ''}
							</span>
							{download.tool && (
								<span className="text-[10px] text-muted-foreground">({download.tool})</span>
							)}
							<span className="text-[10px] text-muted-foreground">
								{formatBytes(download.totalSizeBytes)}
							</span>
						</div>
						<div className="flex items-center gap-1">
							{/* Expand file list for non-media files (json, metadata, etc.) */}
							{nonMediaFiles.length > 0 && (
								<Button
									variant="ghost"
									size="sm"
									className="text-[10px] font-mono text-muted-foreground h-6 px-1.5"
									onClick={() => setShowFileList(!showFileList)}
								>
									{showFileList ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
									{nonMediaFiles.length} other
								</Button>
							)}
							<Button
								variant="ghost"
								size="sm"
								className="text-[10px] font-mono text-muted-foreground h-6 px-1.5"
								onClick={handleDownloadClick}
								disabled={isTriggering}
							>
								<RefreshCw className="size-3 mr-0.5" />
								re-dl
							</Button>
						</div>
					</div>

					{/* Expandable non-media file list */}
					{showFileList && nonMediaFiles.length > 0 && (
						<div className="mt-2 pt-2 border-t space-y-0.5">
							{nonMediaFiles.map((file) => {
								const Icon = fileIcon(file.mimeType);
								return (
									<a
										key={file.filename}
										href={file.url}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-muted transition-colors group text-xs"
									>
										<Icon className="size-3 text-muted-foreground group-hover:text-primary shrink-0" />
										<span className="font-mono truncate flex-1 group-hover:text-primary">
											{file.filename}
										</span>
										<span className="text-muted-foreground shrink-0">
											{formatBytes(file.sizeBytes)}
										</span>
									</a>
								);
							})}
						</div>
					)}
				</div>
			</div>
		);
	}

	// Failed
	if (download.status === 'FAILED') {
		return (
			<div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<AlertTriangle className="size-4 text-destructive" />
						<span className="text-sm font-mono text-destructive">Download failed</span>
						{download.retryCount > 0 && (
							<span className="text-xs text-muted-foreground">
								({download.retryCount} {download.retryCount === 1 ? 'retry' : 'retries'})
							</span>
						)}
					</div>
					<Button
						variant="outline"
						size="sm"
						className="text-xs font-mono"
						onClick={handleRetry}
					>
						<RefreshCw className="size-3 mr-1" />
						[RETRY]
					</Button>
				</div>
				{download.error && (
					<p className="text-xs text-muted-foreground font-mono break-all">
						{download.error}
					</p>
				)}
			</div>
		);
	}

	// Cancelled
	return (
		<div className="rounded-lg border bg-muted/30 p-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<X className="size-4 text-muted-foreground" />
					<span className="text-sm font-mono text-muted-foreground">Cancelled</span>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="text-xs font-mono"
					onClick={handleDownloadClick}
					disabled={isTriggering}
				>
					<Download className="size-3 mr-1" />
					[DOWNLOAD]
				</Button>
			</div>
		</div>
	);
});
